import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImageOff, Box as BoxIcon } from 'lucide-react';
import { Badge, FurnitureIcon } from '@easymoving/ui';
import type { Item } from '@easymoving/types';
import { categoryLabel, destinationLabel, roomLabel } from '../../lib/taxonomy/options';
import { destinationTone, statusTone } from '../../lib/taxonomy/tone';
import { statusLabel } from '../../lib/taxonomy/options';
import { itemVisual } from '../../lib/items/visual';
import { itemLabel } from '../../lib/items/label';

export function ItemCard({ item, boxName }: { item: Item; boxName?: string }) {
  const { t } = useTranslation();
  const room = roomLabel(item.room, t);
  const category = categoryLabel(item.category, t);
  const visual = itemVisual(item, { thumb: true });
  const label = itemLabel(item);

  return (
    <Link
      to={`/app/items/${item.id}`}
      className="group surface overflow-hidden flex flex-col focus-visible:ring-2 focus-visible:ring-petrol/40"
    >
      <div className="aspect-square bg-surface-tint relative overflow-hidden">
        {visual.kind === 'photo' ? (
          <img
            src={visual.url}
            data-storage-path={
              (item.images.find((image) => image.isPrimary) ?? item.images[0])?.thumbPath ??
              (item.images.find((image) => image.isPrimary) ?? item.images[0])?.storagePath
            }
            alt={label}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        ) : visual.kind === 'icon' ? (
          <div className="w-full h-full flex items-center justify-center bg-petrol-tint">
            <FurnitureIcon type={visual.iconType} className="w-1/2 h-1/2" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <ImageOff className="size-8" />
          </div>
        )}
        {item.quantity > 1 && (
          <span className="absolute top-2 start-2 bg-ink/75 text-white text-[11px] font-semibold rounded-full px-2 py-0.5 tnum">
            ×{item.quantity}
          </span>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2">
        <div className="font-medium text-[15px] leading-tight line-clamp-1">{label}</div>
        {(room || category) && (
          <div className="text-[12px] text-graphite-soft line-clamp-1">
            {[room, category].filter(Boolean).join(' · ')}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={destinationTone(item.destination)}>{destinationLabel(item.destination, t)}</Badge>
          <Badge tone={statusTone(item.progressStatus)}>{statusLabel(item.progressStatus, t)}</Badge>
          {item.boxId &&
            (boxName ? (
              // "Where's my stuff": the containing box, by name.
              <span
                className="inline-flex items-center gap-1 text-[11px] text-graphite bg-surface-tint border border-rule rounded-full px-2 py-0.5 max-w-full"
                data-testid="box-chip"
              >
                <BoxIcon className="size-3 shrink-0" />
                <span className="truncate">{boxName}</span>
              </span>
            ) : (
              <span className="inline-flex items-center text-graphite-soft" title={t('item.box')}>
                <BoxIcon className="size-3.5" />
              </span>
            ))}
        </div>
      </div>
    </Link>
  );
}
